import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Form, Button, Alert } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { projectsAPI } from '../../services/api'

const schema = yup.object({
  contract_number: yup.string().required('Номер договора обязателен'),
  address: yup.string().required('Адрес объекта обязателен'),
  customer_name: yup.string().required('ФИО заказчика обязательно'),
  customer_phone: yup.string().required('Номер телефона обязателен'),
  discount_percentage: yup.number()
    .min(0, 'Скидка не может быть отрицательной')
    .max(100, 'Скидка не может быть больше 100%')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  advance_percentage: yup.number()
    .min(0, 'Аванс не может быть отрицательным')
    .max(100, 'Аванс не может быть больше 100%')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  base_rate_adjustment_percentage: yup.number()
    .min(-100, 'Корректировка не может быть меньше -100%')
    .max(500, 'Корректировка не может быть больше 500%')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
}).required()

const ProjectForm = ({ currentUser }) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [project, setProject] = useState(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    if (isEdit) {
      loadProject()
    }
  }, [id])

  const loadProject = async () => {
    try {
      setLoading(true)
      const response = await projectsAPI.get(id)
      const projectData = response.data.project || response.data
      setProject(projectData)
      reset({
        contract_number: projectData.contract_number,
        address: projectData.address,
        customer_name: projectData.customer_name,
        customer_phone: projectData.customer_phone,
        discount_percentage: projectData.discount_percentage,
        advance_percentage: projectData.advance_percentage,
        base_rate_adjustment_percentage: projectData.base_rate_adjustment_percentage,
      })
    } catch (err) {
      setError('Ошибка загрузки проекта')
      console.error('Error loading project:', err)
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (data) => {
    try {
      setLoading(true)
      setError(null)

      const submitData = {
        ...data,
      }

      if (isEdit) {
        await projectsAPI.update(id, submitData)
      } else {
        await projectsAPI.create(submitData)
      }

      navigate('/projects')
    } catch (err) {
      if (err.response?.data?.errors) {
        const errorMessages = Object.entries(err.response.data.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('\n')
        setError(errorMessages)
      } else {
        setError(isEdit ? 'Ошибка при обновлении проекта' : 'Ошибка при создании проекта')
      }
      console.error('Error saving project:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <h1 className="mb-4">
        {isEdit ? 'Редактировать проект' : 'Создать новый проект'}
      </h1>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <h5>Ошибки:</h5>
          <pre className="mb-0">{error}</pre>
        </Alert>
      )}

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Group className="mb-3">
          <Form.Label>Номер договора</Form.Label>
          <Form.Control
            type="text"
            {...register('contract_number')}
            isInvalid={!!errors.contract_number}
          />
          <Form.Control.Feedback type="invalid">
            {errors.contract_number?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Адрес объекта</Form.Label>
          <Form.Control
            type="text"
            {...register('address')}
            isInvalid={!!errors.address}
          />
          <Form.Control.Feedback type="invalid">
            {errors.address?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>ФИО заказчика</Form.Label>
          <Form.Control
            type="text"
            {...register('customer_name')}
            isInvalid={!!errors.customer_name}
          />
          <Form.Control.Feedback type="invalid">
            {errors.customer_name?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Номер телефона заказчика</Form.Label>
          <Form.Control
            type="tel"
            {...register('customer_phone')}
            isInvalid={!!errors.customer_phone}
          />
          <Form.Control.Feedback type="invalid">
            {errors.customer_phone?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Скидка (%)</Form.Label>
          <Form.Control
            type="number"
            step="0.01"
            min="0"
            max="100"
            {...register('discount_percentage')}
            isInvalid={!!errors.discount_percentage}
            placeholder="0.00"
          />
          <Form.Control.Feedback type="invalid">
            {errors.discount_percentage?.message}
          </Form.Control.Feedback>
          <Form.Text className="text-muted">
            Процент скидки от общей стоимости проекта (0-100)
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Аванс (%)</Form.Label>
          <Form.Control
            type="number"
            step="0.01"
            min="0"
            max="100"
            {...register('advance_percentage')}
            isInvalid={!!errors.advance_percentage}
            placeholder="0.00"
          />
          <Form.Control.Feedback type="invalid">
            {errors.advance_percentage?.message}
          </Form.Control.Feedback>
          <Form.Text className="text-muted">
            Процент аванса от стоимости со скидкой (0-100). Отображается только в актах выполненных работ.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Корректировка базовых расценок (%)</Form.Label>
          <Form.Control
            type="number"
            step="0.01"
            min="-100"
            max="500"
            {...register('base_rate_adjustment_percentage')}
            isInvalid={!!errors.base_rate_adjustment_percentage}
            placeholder="0.00"
          />
          <Form.Control.Feedback type="invalid">
            {errors.base_rate_adjustment_percentage?.message}
          </Form.Control.Feedback>
          <Form.Text className="text-muted">
            Все цены в проекте (в интерфейсе и в документах, включая цены для прораба) отображаются с учётом этого процента. Например: 10 — цены +10%, −5 — цены −5%.
          </Form.Text>
        </Form.Group>

        <div className="mt-4">
          <Button type="submit" variant="primary" size="sm" className="me-2" disabled={loading}>
            {loading ? 'Сохранение...' : isEdit ? 'Сохранить изменения' : 'Создать проект'}
          </Button>
          <Link to="/projects" className="btn btn-secondary btn-sm">
            Отмена
          </Link>
        </div>
      </Form>
    </div>
  )
}

export default ProjectForm
